import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import NotFound from "../../../notfound/NotFound";
import {
  hasPermission,
  privilegesEnum,
} from "../../../../helpers/PermissionHelper";

import {
  Form,
  Input,
  message,
  Select,
  Skeleton,
  Table,
  Button,
  Modal,
  Tabs,
} from "antd";
import { RxSketchLogo } from "react-icons/rx";
import CustomBreadcrumb from "../../../../components/breadcrumb/Breadcrumb";
import ActionButtons from "../../../../components/actionbuttons/ActionButtons";

import { getRewardsBasePath } from "../../../../helpers/RewardsPathHelper";
import styles from "./ViewReward.module.scss";
import ViewRewardGeneralTab from "./ViewRewardGeneralTab";
import ViewRewardCategoriesTab from "./ViewRewardCategoriesTab";
import ViewRewardClientsTab from "./ViewRewardClientsTab";
import {
  addSegmentClientsToReward,
  editReward,
  getBrandsFiltered,
  getCategoriesFiltered,
  getClientLevels,
  getClientSegmentsFiltered,
  getRewardById,
  getRewardTypes,
  getClusters,
  getBranchOfficesFiltered,
  getRewardClients,
  getClientsByLikeFilters,
} from "../../../../api/Services";

const ViewReward = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reward, setReward] = useState<any>();
  const [rewardTypes, setRewardTypes] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [clientSegments, setClientSegments] = useState<any[]>([]);
  const [clusters, setClusters] = useState<any[]>([]);
  const [branchOffices, setBranchOffices] = useState<any[]>([]);

  const [clientsPage, setClientsPage] = useState<{
    content: any[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  } | null>(null);
  const [clientsFilters, setClientsFilters] = useState<{
    clientId?: string;
    name?: string;
    email?: string;
    idNumber?: string;
    mobile?: string;
  }>({});
  const [loadingClients, setLoadingClients] = useState(false);
  const [addedClientIds, setAddedClientIds] = useState<number[]>([]);
  const [removedClientIds, setRemovedClientIds] = useState<number[]>([]);
  const [addingSegment, setAddingSegment] = useState(false);
  const [addSegmentId, setAddSegmentId] = useState<number | undefined>();
  const [addClientsModalVisible, setAddClientsModalVisible] = useState(false);
  const [addClientsTab, setAddClientsTab] = useState("segment");
  const [addModalClientFilters, setAddModalClientFilters] = useState<{
    clientId?: string;
    name?: string;
    email?: string;
    idNumber?: string;
    mobile?: string;
  }>({});
  const [clientsSearchResults, setClientsSearchResults] = useState<any[]>([]);
  const [clientsSearchLoading, setClientsSearchLoading] = useState(false);
  const [selectedClientIdsToAdd, setSelectedClientIdsToAdd] = useState<number[]>([]);
  const [addModalClientsPage, setAddModalClientsPage] = useState(0);
  const [addModalClientsTotal, setAddModalClientsTotal] = useState(0);
  const [addModalClientsPageSize, setAddModalClientsPageSize] = useState(5);
  const [activeMainTab, setActiveMainTab] = useState("general");
  const [clientsDirty, setClientsDirty] = useState(false);

  const [editForm] = Form.useForm();

  const canEdit = hasPermission(privilegesEnum.WRITE_REWARDS);

  const { id } = useParams();

  const getBackUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    return `${getRewardsBasePath(location.pathname)}${queryString ? `?${queryString}` : ""}`;
  };



  const handleSearchRewardTypes = useCallback(async (value: string) => {
    const response = await getRewardTypes(1, { name: value });
    setRewardTypes(response.rewardTypes);
  }, []);

  const handleFetchReward = useCallback(async () => {
    let response = await getRewardById(id!);
    setReward(response);
    setBrands(response.brands || []);
    setCategories(response.categories || []);
    setLevels(response.levels || []);
    if (response?.clusters) {
      setClusters((prev) => {
        const map = new Map((prev || []).map((i: any) => [i.id, i]));
        response.clusters.forEach((c: any) => map.set(c.id, c));
        return Array.from(map.values());
      });
    }
    if (response?.branchOffices) {
      setBranchOffices((prev) => {
        const map = new Map((prev || []).map((i: any) => [i.id, i]));
        response.branchOffices.forEach((b: any) => map.set(b.id, b));
        return Array.from(map.values());
      });
    }
    handleSearchRewardTypes(response.type.name);
    setLoading(false);
  }, [id, handleSearchRewardTypes]);

  const handleSearchBrands = async (value: string) => {
    const response = await getBrandsFiltered(value);
    setBrands(response);
  };

  const handleSearchClientSegments = async (value: string) => {
    const response = await getClientSegmentsFiltered(1, { name: value });
    setClientSegments(response.clientSegments);
  };

  const handleSearchLevels = async (value: string) => {
    const response = await getClientLevels(1, { name: value });
    setLevels(response.clientLevels);
  };
  const handleSearchCategories = async (value: string) => {
    const response = await getCategoriesFiltered(value);
    setCategories(response);
  };

  const handleLoadClusters = async () => {
    const response = await getClusters(1);
    setClusters(response.clusters || []);
  };

  const handleSearchBranchOffices = async (value: string) => {
    const response = await getBranchOfficesFiltered(0, { name: value });
    setBranchOffices(response.branchOffices || []);
  };

  const fetchClientsPage = useCallback(
    async (
      page: number,
      size: number,
      filters: {
        clientId?: string;
        name?: string;
        email?: string;
        idNumber?: string;
        mobile?: string;
      }
    ) => {
      if (!id) return;
      setLoadingClients(true);
      try {
        const res = await getRewardClients(id, {
          page,
          size,
          clientId: filters.clientId?.trim() || undefined,
          name: filters.name || undefined,
          email: filters.email || undefined,
          idNumber: filters.idNumber || undefined,
          mobile: filters.mobile || undefined,
        });
        if (res) {
          setClientsPage({
            content: res.content || [],
            totalElements: res.totalElements ?? 0,
            totalPages: res.totalPages ?? 0,
            number: res.number ?? page,
            size: res.size ?? size,
          });
        }
      } finally {
        setLoadingClients(false);
      }
    },
    [id]
  );

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_REWARDS)) {
      handleFetchReward();
      handleSearchRewardTypes("");
      handleLoadClusters();
    }
  }, [handleFetchReward, handleSearchRewardTypes]);

  useEffect(() => {
    if (id) {
      fetchClientsPage(0, 20, clientsFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when id changes
  }, [id]);

  const clientsFiltersDebounceRef = React.useRef(false);
  useEffect(() => {
    if (!id) return;
    if (!clientsFiltersDebounceRef.current) {
      clientsFiltersDebounceRef.current = true;
      return;
    }
    const timer = setTimeout(() => {
      fetchClientsPage(0, clientsPage?.size || 20, clientsFilters);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounced filter: only depend on filters
  }, [clientsFilters]);

  const handleClientsTableChange = (pagination: any) => {
    fetchClientsPage(pagination.current - 1, pagination.pageSize || 20, clientsFilters);
  };

  const handleRemoveClientFromReward = (clientId: number) => {
    setAddedClientIds((prevAdded) => {
      const newAdded = prevAdded.filter((id) => id !== clientId);
      const wasJustAdded = prevAdded.includes(clientId);
      setRemovedClientIds((prevRemoved) =>
        wasJustAdded ? prevRemoved : prevRemoved.includes(clientId) ? prevRemoved : [...prevRemoved, clientId]
      );
      return newAdded;
    });
    setClientsPage((prev) =>
      prev
        ? { ...prev, content: prev.content.filter((c: any) => c.id !== clientId), totalElements: Math.max(0, prev.totalElements - 1) }
        : null
    );
    setClientsDirty(true);
    message.info(t("Common-StagedChangesMessage"));
  };

  const handleOpenAddClientsModal = () => {
    setAddClientsModalVisible(true);
    setAddSegmentId(undefined);
    setAddClientsTab("segment");
    setAddModalClientFilters({});
    setClientsSearchResults([]);
    setSelectedClientIdsToAdd([]);
    setAddModalClientsPage(0);
    setAddModalClientsTotal(0);
    handleSearchClientSegments("");
  };

  const handleCloseAddClientsModal = () => {
    setAddClientsModalVisible(false);
  };

  const handleAddSegmentToReward = async () => {
    if (!addSegmentId) {
      message.warning(t("Common-Select"));
      return;
    }
    setAddingSegment(true);
    try {
      await addSegmentClientsToReward(id!, [addSegmentId], {
        name: reward?.name,
        status: reward?.status,
      });
      message.success(t("Common-EditSuccessMessage"));
      fetchClientsPage(0, clientsPage?.size || 20, clientsFilters);
      handleCloseAddClientsModal();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message_ES ??
        err?.response?.data?.message ??
        err?.message;
      message.error(msg || t("Common-ErrorMessage"));
    } finally {
      setAddingSegment(false);
    }
  };

  const getAddModalClientsFilters = () => ({
    clientId: addModalClientFilters.clientId?.trim() || undefined,
    name: addModalClientFilters.name?.trim() || undefined,
    email: addModalClientFilters.email?.trim() || undefined,
    idNumber: addModalClientFilters.idNumber?.trim() || undefined,
    mobile: addModalClientFilters.mobile?.trim() || undefined,
  });

  const fetchAddModalClientsPage = async (pageIndex: number, pageSize?: number) => {
    const size = pageSize ?? addModalClientsPageSize;
    setClientsSearchLoading(true);
    try {
      const filters = getAddModalClientsFilters();
      const { clients, totalElements } = await getClientsByLikeFilters(pageIndex, size, filters);
      setClientsSearchResults(clients || []);
      setAddModalClientsTotal(totalElements ?? 0);
    } finally {
      setClientsSearchLoading(false);
    }
  };

  const handleSearchClientsForAdd = async () => {
    setAddModalClientsPage(0);
    await fetchAddModalClientsPage(0);
  };

  const handleAddModalClientsTableChange = (page: number, pageSize?: number) => {
    if (pageSize != null && pageSize !== addModalClientsPageSize) {
      setAddModalClientsPageSize(pageSize);
      setAddModalClientsPage(0);
      fetchAddModalClientsPage(0, pageSize);
      return;
    }
    const newPage = page - 1;
    setAddModalClientsPage(newPage);
    fetchAddModalClientsPage(newPage);
  };

  const handleAddSelectedClients = () => {
    if (selectedClientIdsToAdd.length === 0) {
      message.warning(t("Common-Select"));
      return;
    }
    setAddedClientIds((prev) => {
      const set = new Set([...prev, ...selectedClientIdsToAdd]);
      return Array.from(set);
    });
    setRemovedClientIds((prev) => prev.filter((id) => !selectedClientIdsToAdd.includes(id)));
    setClientsDirty(true);
    message.info(t("Common-StagedChangesMessage"));
    fetchClientsPage(0, clientsPage?.size || 20, clientsFilters);
    handleCloseAddClientsModal();
  };

  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const getOptionsCategories = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      const parentsString = item?.parents
        ? item.parents.filter(Boolean).join(" - ")
        : "";
      const categoryName = item?.name || "";

      const categoryTree = parentsString
        ? `${parentsString} - ${categoryName}`
        : categoryName;

      options.push(
        <Select.Option value={item.id} key={item.id}>
          {categoryTree}
        </Select.Option>
      );
    });

    return options;
  };

  const rewardTypesWithCurrent = useMemo(() => {
    const list = rewardTypes || [];
    if (reward?.type && !list.some((t: any) => t.id === reward.type.id)) {
      return [reward.type, ...list];
    }
    return list;
  }, [rewardTypes, reward?.type]);

  const handleEdit = async () => {
    if (!id) return;

    setSaving(true);
    const {
      name,
      status,
      rewardType,
      value,
      pointsValue,
      description,
      validity,
      brandIds,
      categoryIds,
      levelIds,
      clusterIds,
      branchOfficeIds,
    } = editForm.getFieldsValue();

    const validityStart = validity && validity[0] !== null ? validity[0].format("YYYY-MM-DD") : null;
    const validityEnd = validity && validity[1] !== null ? validity[1].format("YYYY-MM-DD") : null;

    const payload = {
      name,
      status,
      rewardType,
      value,
      pointsValue,
      description,
      validityStart,
      validityEnd,
      brandIds,
      categoryIds,
      levelIds,
      articleIds: reward?.articles?.map((art: any) => art.id) || [],
      articleGroupIds: [],
      clusterIds,
      branchOfficeIds,
      codeLength: reward?.codeLength,
      codeType: reward?.codeType,
      prefix: reward?.prefix,
      // Delta only: backend applies add/remove by id so we never load or send the full list
      ...(clientsDirty
        ? {
            addClientIds: addedClientIds,
            removeClientIds: removedClientIds,
          }
        : {}),
    };
    let response = await editReward(id, payload as Parameters<typeof editReward>[1]);
    if (response) {
      message.success(t("Common-EditSuccessMessage"));
      setClientsDirty(false);
      setAddedClientIds([]);
      setRemovedClientIds([]);
      await handleFetchReward();
      fetchClientsPage(0, clientsPage?.size || 20, clientsFilters);
    } else message.error(t("Common-EditErrorMessage"));
    setSaving(false);
  };

  const handleCancelButton = () => {
    editForm.resetFields();
  };

  return loading ? (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <Skeleton.Input style={{ width: 180, height: 20 }} active />
        </div>
        <div className={styles.actionButtonsWrapper}>
          <Skeleton.Button style={{ width: 80, height: 32 }} active />
          <Skeleton.Button style={{ width: 80, height: 32 }} active />
        </div>
      </div>

      <div className={styles.rewardInfoWrapper}>
        <Skeleton active paragraph={{ rows: 12 }} />
      </div>
    </div>
  ) : !reward ? (
    <NotFound />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxSketchLogo />,
                label: t("NavBar-Rewards"),
                path: getBackUrl()
              },
              {
                label: reward?.name || t("Common-Reward"),
                isCurrentPage: true
              }
            ]}
          />
        </div>
        <ActionButtons
          onSave={handleEdit}
          onCancel={handleCancelButton}
          loading={saving}
          disabled={saving}
          show={true}
          permission={privilegesEnum.WRITE_REWARDS}
        />
      </div>

      <div className={styles.rewardInfoWrapper}>
        <Form
          name="editreward"
          form={editForm}
          layout="vertical"
          autoComplete="off"
          className={styles.newForm}
          preserve={false}
        >
          <Tabs activeKey={activeMainTab} onChange={setActiveMainTab}>
            <Tabs.TabPane tab={t("Common-BasicInformation")} key="general">
              <ViewRewardGeneralTab
                reward={reward}
                canEdit={canEdit}
                rewardTypes={rewardTypesWithCurrent}
                onSearchRewardTypes={handleSearchRewardTypes}
                getOptions={getOptions}
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab={t("Common-CategoriesAndSegments")} key="categories">
              <ViewRewardCategoriesTab
                reward={reward}
                canEdit={canEdit}
                brands={brands}
                categories={categories}
                clusters={clusters}
                branchOffices={branchOffices}
                onSearchBrands={handleSearchBrands}
                onSearchCategories={handleSearchCategories}
                onSearchBranchOffices={handleSearchBranchOffices}
                getOptions={getOptions}
                getOptionsCategories={getOptionsCategories}
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab={t("Common-Clients")} key="clients">
              <ViewRewardClientsTab
                reward={reward}
                canEdit={canEdit}
                clientsFilters={clientsFilters}
                setClientsFilters={setClientsFilters}
                clientsPage={clientsPage}
                loadingClients={loadingClients}
                onClientsTableChange={handleClientsTableChange}
                onRemoveClientFromReward={handleRemoveClientFromReward}
                onOpenAddClientsModal={handleOpenAddClientsModal}
                levels={levels}
                onSearchLevels={handleSearchLevels}
                getOptions={getOptions}
              />
            </Tabs.TabPane>
          </Tabs>
        </Form>

        <Modal
          title={t("Common-Add")}
          visible={addClientsModalVisible}
          onCancel={handleCloseAddClientsModal}
          footer={null}
          width={560}
          destroyOnClose
        >
          <Tabs activeKey={addClientsTab} onChange={setAddClientsTab}>
            <Tabs.TabPane tab={t("Common-AddFromSegment")} key="segment">
              <div className={styles.addClientsModalContent}>
                <Select
                  placeholder={t("Common-AddFromSegment")}
                  style={{ width: "100%", marginBottom: 12 }}
                  showSearch
                  onSearch={handleSearchClientSegments}
                  filterOption={(input: string, option: any) =>
                    (option?.label ?? "").toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  options={clientSegments.map((s: any) => ({ value: s.id, label: s.name }))}
                  value={addSegmentId}
                  onChange={setAddSegmentId}
                  allowClear
                />
                <Button
                  type="primary"
                  loading={addingSegment}
                  onClick={handleAddSegmentToReward}
                >
                  {t("Common-Add")}
                </Button>
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane tab={t("Common-Clients")} key="clients">
              <div className={styles.addClientsModalContent}>
                <div className={styles.addModalFiltersRow}>
                  <Input
                    placeholder={t("Common-ClientId")}
                    value={addModalClientFilters.clientId || ""}
                    onChange={(e) =>
                      setAddModalClientFilters((f) => ({
                        ...f,
                        clientId: e.target.value.trim() || undefined,
                      }))
                    }
                    onPressEnter={handleSearchClientsForAdd}
                    allowClear
                    className={styles.addModalFilterInput}
                  />
                  <Input
                    placeholder={t("Common-Name")}
                    value={addModalClientFilters.name || ""}
                    onChange={(e) =>
                      setAddModalClientFilters((f) => ({
                        ...f,
                        name: e.target.value || undefined,
                      }))
                    }
                    onPressEnter={handleSearchClientsForAdd}
                    allowClear
                    className={styles.addModalFilterInput}
                  />
                  <Input
                    placeholder={t("Common-Email")}
                    value={addModalClientFilters.email || ""}
                    onChange={(e) =>
                      setAddModalClientFilters((f) => ({
                        ...f,
                        email: e.target.value || undefined,
                      }))
                    }
                    onPressEnter={handleSearchClientsForAdd}
                    allowClear
                    className={styles.addModalFilterInput}
                  />
                  <Input
                    placeholder={t("Common-IDNumber")}
                    value={addModalClientFilters.idNumber || ""}
                    onChange={(e) =>
                      setAddModalClientFilters((f) => ({
                        ...f,
                        idNumber: e.target.value || undefined,
                      }))
                    }
                    onPressEnter={handleSearchClientsForAdd}
                    allowClear
                    className={styles.addModalFilterInput}
                  />
                  <Input
                    placeholder={t("Common-Mobile")}
                    value={addModalClientFilters.mobile || ""}
                    onChange={(e) =>
                      setAddModalClientFilters((f) => ({
                        ...f,
                        mobile: e.target.value || undefined,
                      }))
                    }
                    onPressEnter={handleSearchClientsForAdd}
                    allowClear
                    className={styles.addModalFilterInput}
                  />
                  <Button
                    type="default"
                    onClick={handleSearchClientsForAdd}
                    loading={clientsSearchLoading}
                  >
                    {t("Common-Search")}
                  </Button>
                </div>
                {(clientsSearchResults.length > 0 || addModalClientsTotal > 0) && (
                  <div className={styles.addModalClientsTableWrapper}>
                    <Table
                      loading={clientsSearchLoading}
                      dataSource={clientsSearchResults}
                      rowKey="id"
                      size="middle"
                      bordered
                      scroll={{ y: 280 }}
                      pagination={{
                        current: addModalClientsPage + 1,
                        pageSize: addModalClientsPageSize,
                        total: addModalClientsTotal,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50"],
                        showTotal: (total) => `${t("Common-Total")} ${total}`,
                        onChange: handleAddModalClientsTableChange,
                        locale: {
                          items_per_page: t("Common-PaginationPerPage"),
                        },
                      }}
                      rowSelection={{
                        selectedRowKeys: selectedClientIdsToAdd,
                        onChange: (keys: React.Key[]) =>
                          setSelectedClientIdsToAdd(keys as number[]),
                      }}
                      columns={[
                        {
                          title: t("Common-ClientId"),
                          dataIndex: "id",
                          key: "id",
                          width: 80,
                          align: "right",
                        },
                        {
                          title: t("Common-IDNumber"),
                          dataIndex: "idNumber",
                          key: "idNumber",
                          width: 120,
                          render: (val: string) => val || "—",
                        },
                        {
                          title: t("Common-Name"),
                          dataIndex: "name",
                          key: "name",
                          render: (_: any, r: any) =>
                            `${r.name || ""} ${r.lastName || ""}`.trim() || "—",
                        },
                        {
                          title: t("Common-Email"),
                          dataIndex: "email",
                          key: "email",
                          render: (val: string) => val || "—",
                        },
                        {
                          title: t("Common-Mobile"),
                          dataIndex: "mobile",
                          key: "mobile",
                          width: 110,
                          render: (val: string) => val || "—",
                        },
                      ]}
                    />
                  </div>
                )}
                <Button
                  type="primary"
                  disabled={selectedClientIdsToAdd.length === 0}
                  onClick={handleAddSelectedClients}
                >
                  {t("Common-Add")} ({selectedClientIdsToAdd.length})
                </Button>
              </div>
            </Tabs.TabPane>
          </Tabs>
        </Modal>
      </div>
    </div>
  );
};

export default ViewReward;
